import { metrics } from '@opentelemetry/api'
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web'
import { ZoneContextManager } from '@opentelemetry/context-zone'
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { resourceFromAttributes } from '@opentelemetry/resources'
import {
    MeterProvider,
    PeriodicExportingMetricReader,
} from '@opentelemetry/sdk-metrics'
import {
    BatchSpanProcessor,
    WebTracerProvider,
} from '@opentelemetry/sdk-trace-web'
import {
    ATTR_SERVICE_NAME,
    ATTR_SERVICE_VERSION,
} from '@opentelemetry/semantic-conventions'
import { version } from '../../../package.json'

const otelEnabled = import.meta.env.VITE_OTEL_ENABLED !== 'false'

if (otelEnabled) {
    const resource = resourceFromAttributes({
        [ATTR_SERVICE_NAME]:
            import.meta.env.VITE_OTEL_SERVICE_NAME ?? 'pontoagil-web',
        [ATTR_SERVICE_VERSION]: version ?? 'unknown',
        'deployment.environment':
            import.meta.env.VITE_DEPLOYMENT_ENV ?? 'unknown',
    })

    const traceExporter = new OTLPTraceExporter({
        url: resolveTraceEndpoint(),
    })

    const provider = new WebTracerProvider({
        resource,
        spanProcessors: [new BatchSpanProcessor(traceExporter)],
    })

    provider.register({
        contextManager: new ZoneContextManager(),
    })

    const meterProvider = new MeterProvider({
        resource,
        readers: [
            new PeriodicExportingMetricReader({
                exporter: new OTLPMetricExporter({
                    url: resolveMetricsEndpoint(),
                }),
                exportIntervalMillis: 30_000,
            }),
        ],
    })

    metrics.setGlobalMeterProvider(meterProvider)

    registerInstrumentations({
        instrumentations: [
            getWebAutoInstrumentations({
                '@opentelemetry/instrumentation-fetch': {
                    propagateTraceHeaderCorsUrls: resolvePropagatedUrls(),
                },
                '@opentelemetry/instrumentation-xml-http-request': {
                    propagateTraceHeaderCorsUrls: resolvePropagatedUrls(),
                },
            }),
        ],
    })
}

export function resolveTraceEndpoint() {
    const exporterEndpoint =
        import.meta.env.VITE_OTEL_EXPORTER_OTLP_TRACES_ENDPOINT ??
        import.meta.env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT ??
        'http://localhost:4318'

    const normalizedEndpoint = exporterEndpoint.replace(/\/+$/, '')

    if (normalizedEndpoint.endsWith('/v1/traces')) {
        return normalizedEndpoint
    }

    return `${normalizedEndpoint}/v1/traces`
}

function resolveMetricsEndpoint() {
    const exporterEndpoint =
        import.meta.env.VITE_OTEL_EXPORTER_OTLP_METRICS_ENDPOINT ??
        import.meta.env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT ??
        'http://localhost:4318'

    const normalizedEndpoint = exporterEndpoint.replace(/\/+$/, '')

    if (normalizedEndpoint.endsWith('/v1/metrics')) {
        return normalizedEndpoint
    }

    return `${normalizedEndpoint}/v1/metrics`
}

function resolvePropagatedUrls() {
    const configuredUrls = import.meta.env.VITE_OTEL_PROPAGATE_TRACE_TO?.split(
        ',',
    )
        .map(url => url.trim())
        .filter(Boolean)

    if (configuredUrls?.length) {
        return configuredUrls
    }

    const defaultUrls = [import.meta.env.VITE_BASE_URL, window.location.origin]

    return defaultUrls.filter(Boolean)
}
